import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, Users, ShoppingCart, Calendar, Clock, Search, RefreshCw, Activity, CreditCard, DollarSign, User, MapPin, ChevronRight } from 'lucide-react';
import { formatDate as formatDateForDisplay, formatDateForInput } from '../utils/dateUtils';
import MaskedDatePicker from './MaskedDatePicker';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import dashboardService from '../services/dashboardService';

const DashboardComponent = ({ onNavigateToTopProducts, onNavigateToSalesOverview, onNavigateToInactiveCredit }) => {
  // Date Range State
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Hourly Sales State
  const [hourlySalesData, setHourlySalesData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);

  // Team Sales Total State (combined from all branches)
  const [teamSalesTotal, setTeamSalesTotal] = useState(null);
  const [teamSalesLoading, setTeamSalesLoading] = useState(false);
  const [categorizedProducts, setCategorizedProducts] = useState({});
  const [factoryTypes, setFactoryTypes] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Daily Sales State
  const [dailySalesData, setDailySalesData] = useState([]);
  const [isDailyLoading, setIsDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState(null);

  // Customer Statistics State
  const [customerStats, setCustomerStats] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);

  // Sales Staff Statistics State
  const [salesmanStats, setSalesmanStats] = useState(null);
  const [salesmanLoading, setSalesmanLoading] = useState(false);

  // Customer Segmentation State (fixed: 2025-12-01 to today)
  const [customerSegmentation, setCustomerSegmentation] = useState(null);
  const [segmentationLoading, setSegmentationLoading] = useState(false);

  // Helper function to get date range
  const getDateRange = useCallback((days) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);
    
    return {
      startDate: formatDateForInput(startDate),
      endDate: formatDateForInput(endDate)
    };
  }, []);

  // Helper function to format numbers with Thai localization
  const formatNumber = (num) => {
    return new Intl.NumberFormat('th-TH').format(num);
  };

  // Initialize with last 7 days
  useEffect(() => {
    const initialRange = getDateRange(7);
    setDateRange(initialRange);
  }, [getDateRange]);

  // Load initial data
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchHourlySalesData(dateRange.startDate, dateRange.endDate);
      fetchDailySalesData(dateRange.startDate, dateRange.endDate);
      fetchTopProductsData(dateRange.startDate, dateRange.endDate);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  // Load customer statistics on component mount
  useEffect(() => {
    fetchCustomerStats();
    fetchCustomerSegmentation();
  }, []);

  // Load salesman statistics when date range changes
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchSalesmanStats(dateRange.startDate, dateRange.endDate);
      fetchTeamSalesTotal(dateRange.startDate, dateRange.endDate);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  // Handle quick date range selection
  const handleQuickRange = (days) => {
    const range = getDateRange(days);
    setDateRange(range);
  };

  // Handle manual date input
  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fetch hourly sales data
  const fetchHourlySalesData = async (startDate, endDate) => {
    if (!startDate || !endDate) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await dashboardService.getHourlySales(startDate, endDate);
      
      if (result.success) {
        // Transform data for chart (Elite API response)
        const transformedData = result.data.map(item => ({
          hour: item.hour_display,
          hour_range: item.hour_range,
          hour_label: item.hour_label,
          quotation_value: item.total_quotation_value,
          invoice_value: item.total_invoice_value,
          quotation_count: item.quotation_count,
          invoice_count: item.invoice_count,
          avg_quotation_value: item.avg_quotation_value,
          avg_invoice_value: item.avg_invoice_value,
          conversion_rate: item.conversion_success_rate,
          business_score: item.business_intelligence_score
        }));
        setHourlySalesData(transformedData);
        
        // Store summary data
        if (result.executive_summary) {
          setSummaryData(result.executive_summary);
        }
      } else {
        setError(result.error || 'Failed to fetch data');
        setHourlySalesData([]);
      }
    } catch (err) {
      setError('Network error occurred');
      setHourlySalesData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch daily sales data
  const fetchDailySalesData = async (startDate, endDate) => {
    if (!startDate || !endDate) return;
    
    setIsDailyLoading(true);
    setDailyError(null);
    
    try {
      const result = await dashboardService.getDailySales(startDate, endDate);
      
      if (result.success) {
        // Transform data for chart (Elite API response)
        const transformedData = result.data.map(item => ({
          date: item.date,
          date_display: item.date ? item.date.split('-').reverse().join('/') : item.date_display,
          date_label: item.date_label,
          day_of_week: item.day_of_week,
          quotation_value: item.total_quotation_value,
          invoice_value: item.total_invoice_value,
          quotation_count: item.quotation_count,
          invoice_count: item.invoice_count,
          avg_quotation_value: item.avg_quotation_value,
          avg_invoice_value: item.avg_invoice_value,
          conversion_rate: item.conversion_success_rate,
          daily_score: item.daily_performance_score,
          cash_amount: item.cash_amount || 0,
          credit_amount: item.credit_amount || 0,
          cash_count: item.cash_count || 0,
          credit_count: item.credit_count || 0,
        }));
        setDailySalesData(transformedData);
      } else {
        setDailyError(result.error || 'Failed to fetch data');
        setDailySalesData([]);
      }
    } catch (err) {
      setDailyError('Network error occurred');
      setDailySalesData([]);
    } finally {
      setIsDailyLoading(false);
    }
  };

  // Fetch top products data with factory type categorization
  const fetchTopProductsData = async (startDate, endDate) => {
    if (!startDate || !endDate) return;
    
    try {
      const result = await dashboardService.getTopProducts(startDate, endDate, 100);
      
      if (result.success) {
        // Filter out X0100001
        const filteredData = result.data.filter(product => 
          product.product_code && product.product_code.trim() !== 'X0100001'
        );
        
        // Fetch factory types for the products
        const factoryTypesResult = await dashboardService.getFactoryTypesForProducts(filteredData);
        setFactoryTypes(factoryTypesResult);
        
        // Categorize products by factory type and limit to 4 main categories
        const categorized = {};
        
        filteredData.forEach(product => {
          const productCode = product.product_code.trim();
          let factoryType = factoryTypesResult[productCode]?.factory_type || 'Other';
          
          // Map to 4 main categories
          let normalizedType;
          const lowerFactoryType = factoryType.toLowerCase();
          if (lowerFactoryType === 'satellite' || lowerFactoryType === 'sat') {
            normalizedType = 'SAT';
          } else if (lowerFactoryType === 'solar') {
            normalizedType = 'Solar';
          } else if (lowerFactoryType === 'mtf') {
            normalizedType = 'MTF';
          } else if (lowerFactoryType === 'mtb') {
            normalizedType = 'MTB';
          } else {
            // รวมทุกอย่างที่ไม่ใช่ sat, solar, mtf, mtb ไว้ใน Other
            normalizedType = 'Other';
          }
          
          if (!categorized[normalizedType]) {
            categorized[normalizedType] = [];
          }
          categorized[normalizedType].push({
            ...product,
            factory_type: normalizedType,
            factory_name_products: factoryTypesResult[productCode]?.factory_name_products || ''
          });
        });
        
        setCategorizedProducts(categorized);
      } else {
        console.error('Failed to fetch top products:', result.error);
        setCategorizedProducts({});
        setFactoryTypes({});
      }
    } catch (err) {
      console.error('Error fetching top products:', err);
      setCategorizedProducts({});
      setFactoryTypes({});
    }
  };

  // Fetch customer statistics data
  const fetchCustomerStats = async () => {
    setCustomerLoading(true);
    try {
      const response = await fetch(
        'https://apisaleeasy.psisat.com/api/credit-limit-mismatch/stats',
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setCustomerStats(result.data);
      } else {
        throw new Error(result.message || 'เกิดข้อผิดพลาดในการดึงข้อมูลลูกค้า');
      }
    } catch (err) {
      console.error('Error fetching customer stats:', err);
    } finally {
      setCustomerLoading(false);
    }
  };

  // Fetch salesman statistics data
  const fetchSalesmanStats = async (startDate, endDate) => {
    if (!startDate || !endDate) return;
    
    setSalesmanLoading(true);
    try {
      const response = await fetch(
        'https://apisaleeasy.psisat.com/api/sales-analytics/salesman-summary',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            "date_range": {
              "start_date": startDate,
              "end_date": endDate,
              "group_by": "month"
            },
            "filters": {
              "company_code": "045"
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status) {
        setSalesmanStats(result.data);
      } else {
        throw new Error(result.result_desc || 'เกิดข้อผิดพลาดในการดึงข้อมูลพนักงานขาย');
      }
    } catch (err) {
      console.error('Error fetching salesman stats:', err);
      setSalesmanStats(null);
    } finally {
      setSalesmanLoading(false);
    }
  };

  // Fetch combined team sales totals from all branches via single API
  const fetchTeamSalesTotal = async (startDate, endDate) => {
    if (!startDate || !endDate) return;

    setTeamSalesLoading(true);
    try {
      const response = await fetch(
        'https://apisaleeasy.psisat.com/api/sales-team/combined-sales-summary',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date_range: {
              start_date: startDate,
              end_date: endDate
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status) {
        setTeamSalesTotal({
          totalAmount: result.data.total_amount,
          totalBills: result.data.total_bills
        });
      } else {
        throw new Error(result.result_desc || 'Failed to fetch combined sales summary');
      }
    } catch (err) {
      console.error('Error fetching team sales total:', err);
      setTeamSalesTotal(null);
    } finally {
      setTeamSalesLoading(false);
    }
  };

  // Fetch customer segmentation (fixed date: 2025-12-01 to today)
  const fetchCustomerSegmentation = async () => {
    setSegmentationLoading(true);
    try {
      const todayStr = formatDateForInput(new Date());
      const response = await fetch(
        'https://apisaleeasy.psisat.com/api/sales-team/team-sales-summary',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date_range: { start_date: '2025-12-01', end_date: todayStr },
            filters: { company_code: '045' }
          })
        }
      );
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const result = await response.json();
      if (result.status) {
        const breakdown = result.data.summary.customer_breakdown_by_type || {};
        const allCustomers = [];
        Object.entries(breakdown).forEach(([type, customers]) => {
          customers.forEach(c => allCustomers.push({ ...c, customerType: type }));
        });
        const totalNet = allCustomers.reduce((s, c) => s + c.net_amount, 0);
        const large = allCustomers.filter(c => c.net_amount >= 1000000);
        const medium = allCustomers.filter(c => c.net_amount >= 50000 && c.net_amount < 1000000);
        const small = allCustomers.filter(c => c.net_amount < 50000);
        const repeatBuyers = allCustomers.filter(c => c.invoice_count >= 2).length;
        const maCustomers = breakdown.ma || [];
        const normalCustomers = breakdown.normal || [];
        const newCustomers = breakdown.new || [];
        setCustomerSegmentation({
          totalCustomers: allCustomers.length,
          totalNet,
          large: { count: large.length, net: large.reduce((s, c) => s + c.net_amount, 0) },
          medium: { count: medium.length, net: medium.reduce((s, c) => s + c.net_amount, 0) },
          small: { count: small.length, net: small.reduce((s, c) => s + c.net_amount, 0) },
          repeatBuyers,
          oneTimeBuyers: allCustomers.length - repeatBuyers,
          ma: { count: maCustomers.length, net: maCustomers.reduce((s, c) => s + c.net_amount, 0) },
          normal: { count: normalCustomers.length, net: normalCustomers.reduce((s, c) => s + c.net_amount, 0) },
          newCust: { count: newCustomers.length, net: newCustomers.reduce((s, c) => s + c.net_amount, 0) },
        });
      }
    } catch (err) {
      console.error('Error fetching customer segmentation:', err);
    } finally {
      setSegmentationLoading(false);
    }
  };

  // Handle apply button click
  const handleApplyDateRange = () => {
    fetchHourlySalesData(dateRange.startDate, dateRange.endDate);
    fetchDailySalesData(dateRange.startDate, dateRange.endDate);
    fetchTopProductsData(dateRange.startDate, dateRange.endDate);
    fetchSalesmanStats(dateRange.startDate, dateRange.endDate);
    fetchTeamSalesTotal(dateRange.startDate, dateRange.endDate);
  };

  // Handle refresh button click
  const handleRefresh = () => {
    fetchHourlySalesData(dateRange.startDate, dateRange.endDate);
    fetchDailySalesData(dateRange.startDate, dateRange.endDate);
    fetchTopProductsData(dateRange.startDate, dateRange.endDate);
    fetchSalesmanStats(dateRange.startDate, dateRange.endDate);
    fetchTeamSalesTotal(dateRange.startDate, dateRange.endDate);
  };

  // Custom tooltip for hourly chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-gray-300 rounded-lg shadow-lg">
          <div className="mb-2">
            <p className="font-semibold text-gray-900">{`ช่วงเวลา: ${data.hour_range}`}</p>
            <p className="text-sm text-gray-600">{`(${data.hour_label})`}</p>
          </div>
          <div className="space-y-1">
            <p className="text-blue-600">{`ยอดใบเสนอราคา: ${data.quotation_value.toLocaleString()}`}</p>
            <p className="text-green-600">{`ยอดใบแจ้งหนี้: ${data.invoice_value.toLocaleString()}`}</p>
            <p className="text-purple-600">{`จำนวนใบเสนอราคา: ${data.quotation_count}`}</p>
            <p className="text-orange-600">{`จำนวนใบแจ้งหนี้: ${data.invoice_count}`}</p>
            <p className="text-indigo-600">{`อัตราแปลงสถานะ: ${data.conversion_rate}%`}</p>
            <p className="text-cyan-600">{`คะแนนประสิทธิภาพ: ${data.business_score.toFixed(1)}`}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for daily chart
  const CustomDailyTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const totalPayment = (data.cash_amount || 0) + (data.credit_amount || 0);
      const cashPct = totalPayment > 0 ? ((data.cash_amount / totalPayment) * 100).toFixed(1) : 0;
      const creditPct = totalPayment > 0 ? ((data.credit_amount / totalPayment) * 100).toFixed(1) : 0;
      return (
        <div className="bg-white p-4 border border-gray-300 rounded-lg shadow-lg min-w-[220px]">
          <div className="mb-2">
            <p className="font-semibold text-gray-900">{`วันที่: ${data.date_label}`}</p>
            <p className="text-sm text-gray-600">{`(${data.day_of_week})`}</p>
          </div>
          <div className="space-y-1 text-sm">
            <p className="text-green-600 font-medium">{`ยอดขายรวม: ${data.invoice_value.toLocaleString()}`}</p>
            <hr className="border-gray-200 my-1" />
            <p className="text-emerald-600">{`เงินสด: ${(data.cash_amount || 0).toLocaleString()} (${cashPct}%)`}</p>
            <p className="text-orange-500">{`เครดิต: ${(data.credit_amount || 0).toLocaleString()} (${creditPct}%)`}</p>
            <hr className="border-gray-200 my-1" />
            <p className="text-gray-500">{`บิลเงินสด: ${data.cash_count || 0} ใบ | บิลเครดิต: ${data.credit_count || 0} ใบ`}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-full">
      <div className="max-w-7xl mx-auto">
        {/* Date Range Filter */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Date Range Inputs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-600" />
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">วันที่เริ่มต้น:</label>
                <MaskedDatePicker
                  value={dateRange.startDate}
                  onChange={(str) => handleDateChange('startDate', str)}
                  placeholderText="DD/MM/YYYY"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-600" />
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">วันที่สิ้นสุด:</label>
                <MaskedDatePicker
                  value={dateRange.endDate}
                  onChange={(str) => handleDateChange('endDate', str)}
                  placeholderText="DD/MM/YYYY"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Quick Range Buttons and Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleQuickRange(7)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
              >
                <Clock className="w-4 h-4" />
                7 วันย้อนหลัง
              </button>
              <button
                onClick={() => handleQuickRange(30)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors"
              >
                <Clock className="w-4 h-4" />
                30 วันย้อนหลัง
              </button>
              
              {/* Divider */}
              <div className="w-px bg-gray-300 mx-2"></div>
              
              {/* Apply Button */}
              <button
                onClick={handleApplyDateRange}
                className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
              >
                <Search className="w-4 h-4" />
                เลือก
              </button>
              
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors shadow-sm"
              >
                <RefreshCw className="w-4 h-4" />
                รีเฟรช
              </button>
            </div>
          </div>
          
          {/* Selected Range Display */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              <strong>ช่วงวันที่ที่เลือก:</strong> {formatDateForDisplay(dateRange.startDate) || 'ยังไม่ได้เลือก'} ถึง {formatDateForDisplay(dateRange.endDate) || 'ยังไม่ได้เลือก'}
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Card 1 - Total Sales */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img src="/payment.png" alt="Payment" className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">ยอดขายรวมทุกทีม</h3>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-xl font-bold text-blue-600">
                {teamSalesLoading ? (
                  <span className="text-sm text-gray-400">กำลังโหลด...</span>
                ) : teamSalesTotal ? `${teamSalesTotal.totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท` : '--'}
              </div>
            </div>
          </div>

          {/* Card 2 - Total Bills */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img src="/invoice.png" alt="Invoice" className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">จำนวนบิลรวมทุกทีม</h3>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-2xl font-bold text-green-600">
                {teamSalesLoading ? (
                  <span className="text-sm text-gray-400">กำลังโหลด...</span>
                ) : teamSalesTotal ? teamSalesTotal.totalBills.toLocaleString('th-TH') : '--'}
              </div>
            </div>
          </div>

          {/* Card 3 - Average Sale */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">ยอดขายเฉลี่ยต่อบิล</h3>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-2xl font-bold text-purple-600">
                {teamSalesTotal && teamSalesTotal.totalBills > 0 ? `${(teamSalesTotal.totalAmount / teamSalesTotal.totalBills).toLocaleString('th-TH', { maximumFractionDigits: 0 })}` : '--'}
              </div>
            </div>
          </div>

        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Daily Sales Chart Card */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center">
                  <img src="/growth.png" alt="Growth" className="w-10 h-10" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">ยอดขายรายวัน</h3>
                  <p className="text-xs text-gray-600">
                    {formatDateForDisplay(dateRange.startDate)} ถึง {formatDateForDisplay(dateRange.endDate)}
                  </p>
                </div>
              </div>
              {isDailyLoading && (
                <div className="flex items-center gap-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm">กำลังโหลด...</span>
                </div>
              )}
            </div>

            {dailyError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">เกิดข้อผิดพลาด: {dailyError}</p>
              </div>
            )}

            <div className="h-56">
              {dailySalesData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailySalesData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date_display"
                      stroke="#6b7280"
                      fontSize={10}
                      tick={{ fill: '#6b7280' }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      stroke="#6b7280"
                      fontSize={12}
                      tick={{ fill: '#6b7280' }}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip content={<CustomDailyTooltip />} />
                    <Legend
                      verticalAlign="top"
                      height={24}
                      formatter={(value) => value === 'cash_amount' ? 'เงินสด' : 'เครดิต'}
                    />
                    <Bar dataKey="cash_amount" name="cash_amount" stackId="payment" fill="#10b981" stroke="#059669" strokeWidth={1} />
                    <Bar dataKey="credit_amount" name="credit_amount" stackId="payment" fill="#f97316" stroke="#ea580c" strokeWidth={1} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium mb-2">ไม่มีข้อมูล</h3>
                    <p>กรุณาเลือกช่วงวันที่และกดปุ่ม "เลือก"</p>
                  </div>
                </div>
              )}
            </div>

            {/* Cash / Credit Summary Footer */}
            {dailySalesData.length > 0 && (() => {
              const totalCash = dailySalesData.reduce((sum, d) => sum + (d.cash_amount || 0), 0);
              const totalCredit = dailySalesData.reduce((sum, d) => sum + (d.credit_amount || 0), 0);
              const total = totalCash + totalCredit;
              return (
                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 bg-emerald-50 rounded-lg px-4 py-3">
                    <div className="w-3 h-3 rounded-sm bg-emerald-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-emerald-700 font-medium">เงินสด</p>
                      <p className="text-sm font-bold text-emerald-800">{formatNumber(Math.round(totalCash))}</p>
                      <p className="text-xs text-emerald-600">{total > 0 ? ((totalCash / total) * 100).toFixed(1) : 0}%</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-orange-50 rounded-lg px-4 py-3">
                    <div className="w-3 h-3 rounded-sm bg-orange-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-orange-700 font-medium">เครดิต</p>
                      <p className="text-sm font-bold text-orange-800">{formatNumber(Math.round(totalCredit))}</p>
                      <p className="text-xs text-orange-600">{total > 0 ? ((totalCredit / total) * 100).toFixed(1) : 0}%</p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Hourly Sales Chart Card */}
          <div className="lg:col-span-1 bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center">
                  <img src="/clock.png" alt="Clock" className="w-10 h-10" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">ช่วงเวลาที่มีการเปิดบิล</h3>
                  <p className="text-xs text-gray-600">
                    {formatDateForDisplay(dateRange.startDate)} ถึง {formatDateForDisplay(dateRange.endDate)}
                  </p>
                </div>
              </div>
              {isLoading && (
                <div className="flex items-center gap-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm">กำลังโหลด...</span>
                </div>
              )}
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">เกิดข้อผิดพลาด: {error}</p>
              </div>
            )}

            <div className="h-56">
              {hourlySalesData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourlySalesData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="hour" 
                      stroke="#6b7280"
                      fontSize={12}
                      tick={{ fill: '#6b7280' }}
                    />
                    <YAxis 
                      stroke="#6b7280"
                      fontSize={12}
                      tick={{ fill: '#6b7280' }}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey="invoice_value" 
                      fill="#3b82f6" 
                      stroke="#2563eb"
                      strokeWidth={1}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <Activity className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium mb-2">ไม่มีข้อมูล</h3>
                    <p>กรุณาเลือกช่วงวันที่และกดปุ่ม "เลือก"</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Customer Statistics and Sales Staff Statistics Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Customer Statistics Card */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center">
              <img src="/working-time.png" alt="Working Time" className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">สถิติลูกค้า</h3>
          </div>
          
          {customerLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-3"></div>
              <span className="text-gray-500">กำลังโหลด...</span>
            </div>
          ) : customerStats ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-gray-600" />
                    <h4 className="text-xs font-medium text-gray-700">ลูกค้าทั้งหมด</h4>
                  </div>
                  <div className="text-xl font-bold text-gray-900">
                    {formatNumber(customerStats.technician_stats.total_technicians)}
                  </div>
                </div>

                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-gray-600" />
                    <h4 className="text-xs font-medium text-gray-700">ลูกค้าที่มีเครดิต</h4>
                  </div>
                  <div className="text-xl font-bold text-gray-900">
                    {formatNumber(customerStats.customer_credit_usage_stats.customers_with_credit)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-gray-600" />
                    <h4 className="text-xs font-medium text-gray-700">ลูกค้าใช้เครดิต</h4>
                  </div>
                  <div className="text-xl font-bold text-green-600">
                    {formatNumber(customerStats.customer_credit_usage_stats.customers_using_credit)}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    ({customerStats.customer_credit_usage_stats.credit_usage_percentage}%)
                  </div>
                </div>

                <div
                  className={`text-center rounded-lg p-2 -m-2 transition-colors ${
                    onNavigateToInactiveCredit ? 'cursor-pointer hover:bg-orange-50' : ''
                  }`}
                  onClick={onNavigateToInactiveCredit}
                  role={onNavigateToInactiveCredit ? 'button' : undefined}
                  title={onNavigateToInactiveCredit ? 'ดูรายชื่อลูกค้า' : undefined}
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-gray-600" />
                    <h4 className="text-xs font-medium text-gray-700">ลูกค้าที่ยังไม่ใช้เครดิต</h4>
                    {onNavigateToInactiveCredit && <ChevronRight className="w-3 h-3 text-gray-400" />}
                  </div>
                  <div className="text-xl font-bold text-orange-600">
                    {formatNumber(customerStats.customer_credit_usage_stats.customers_inactive_credit)}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    ({customerStats.customer_credit_usage_stats.inactive_credit_percentage}%) · ไม่เปิดบิล {customerStats.customer_credit_usage_stats.inactive_months_window} เดือน
                  </div>
                  {onNavigateToInactiveCredit && (
                    <div className="text-xs text-orange-600 mt-1 font-medium">ดูรายชื่อ →</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pb-2">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-gray-600" />
                    <h4 className="text-xs font-medium text-gray-700">วงเงินเครดิตที่ให้ลูกค้า</h4>
                  </div>
                  <div className="text-xl font-bold text-blue-600">
                    {customerStats.technician_stats ? `${formatNumber(customerStats.technician_stats.total_credit_limit)}` : '--'}
                  </div>
                </div>

                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-gray-600" />
                    <h4 className="text-xs font-medium text-gray-700">เครดิตที่ลูกค้าใช้ทั้งหมด</h4>
                  </div>
                  <div className="text-xl font-bold text-green-600">
                    {customerStats.technician_stats ? `${formatNumber(customerStats.technician_stats.total_credit_limit_remaining)}` : '--'}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              ไม่สามารถโหลดข้อมูลลูกค้าได้
            </div>
          )}
          </div>

          {/* Sales Staff Statistics Card */}
          <div 
            className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 cursor-pointer hover:shadow-xl transition-shadow duration-300"
            onClick={onNavigateToSalesOverview}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center">
                <img src="/teamwork.png" alt="Teamwork" className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">ข้อมูลพนักงานขาย</h3>
            </div>
            
            {salesmanLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-3"></div>
                <span className="text-gray-500">กำลังโหลด...</span>
              </div>
            ) : salesmanStats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-gray-600" />
                      <h4 className="text-xs font-medium text-gray-700">พนักงานขายทั้งหมด</h4>
                    </div>
                    <div className="text-xl font-bold text-gray-900">
                      {formatNumber(salesmanStats.summary.total_salesmen)}
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Activity className="w-4 h-4 text-gray-600" />
                      <h4 className="text-xs font-medium text-gray-700">ยอดขายรวม</h4>
                    </div>
                    <div className="text-xl font-bold text-blue-600">
                      {formatNumber(salesmanStats.summary.total_amount)}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                ไม่สามารถโหลดข้อมูลพนักงานขายได้
              </div>
            )}
          </div>
        </div>

        {/* Customer Segmentation Card */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 flex items-center justify-center">
              <Users className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">วิเคราะห์กลุ่มลูกค้า</h3>
              <p className="text-xs text-gray-500">ตั้งแต่เริ่มระบบ (ธ.ค. 68 – ปัจจุบัน)</p>
            </div>
          </div>

          {segmentationLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-3"></div>
              <span className="text-gray-500">กำลังโหลด...</span>
            </div>
          ) : customerSegmentation ? (
            <div className="space-y-5">
              {/* Tier Breakdown */}
              <div className="grid grid-cols-3 gap-3">
                {/* รายใหญ่ */}
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center">
                  <div className="text-xs font-semibold text-amber-700 mb-1">รายใหญ่</div>
                  <div className="text-xs text-amber-500 mb-2">≥ 1,000,000 บาท</div>
                  <div className="text-2xl font-bold text-amber-700">
                    {customerSegmentation.large.count.toLocaleString('th-TH')}
                  </div>
                  <div className="text-xs text-amber-600 mt-1">ราย</div>
                  <div className="mt-2 text-xs font-medium text-amber-700">
                    {customerSegmentation.totalNet > 0
                      ? ((customerSegmentation.large.net / customerSegmentation.totalNet) * 100).toFixed(1)
                      : 0}% of revenue
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {(customerSegmentation.large.net / 1000000).toFixed(1)}M บาท
                  </div>
                </div>

                {/* รายกลาง */}
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-center">
                  <div className="text-xs font-semibold text-blue-700 mb-1">รายกลาง</div>
                  <div className="text-xs text-blue-500 mb-2">50,000 – 999,999 บาท</div>
                  <div className="text-2xl font-bold text-blue-700">
                    {customerSegmentation.medium.count.toLocaleString('th-TH')}
                  </div>
                  <div className="text-xs text-blue-600 mt-1">ราย</div>
                  <div className="mt-2 text-xs font-medium text-blue-700">
                    {customerSegmentation.totalNet > 0
                      ? ((customerSegmentation.medium.net / customerSegmentation.totalNet) * 100).toFixed(1)
                      : 0}% of revenue
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {(customerSegmentation.medium.net / 1000000).toFixed(1)}M บาท
                  </div>
                </div>

                {/* รายเล็ก */}
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-center">
                  <div className="text-xs font-semibold text-gray-600 mb-1">รายเล็ก</div>
                  <div className="text-xs text-gray-400 mb-2">&lt; 50,000 บาท</div>
                  <div className="text-2xl font-bold text-gray-700">
                    {customerSegmentation.small.count.toLocaleString('th-TH')}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">ราย</div>
                  <div className="mt-2 text-xs font-medium text-gray-600">
                    {customerSegmentation.totalNet > 0
                      ? ((customerSegmentation.small.net / customerSegmentation.totalNet) * 100).toFixed(1)
                      : 0}% of revenue
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {(customerSegmentation.small.net / 1000000).toFixed(1)}M บาท
                  </div>
                </div>
              </div>

              {/* Repeat vs One-time */}
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-semibold text-green-700">
                    Repeat buyers: {customerSegmentation.repeatBuyers.toLocaleString('th-TH')} ราย ({customerSegmentation.totalCustomers > 0 ? ((customerSegmentation.repeatBuyers / customerSegmentation.totalCustomers) * 100).toFixed(1) : 0}%)
                  </span>
                  <span className="text-xs font-semibold text-orange-600">
                    One-time: {customerSegmentation.oneTimeBuyers.toLocaleString('th-TH')} ราย ({customerSegmentation.totalCustomers > 0 ? ((customerSegmentation.oneTimeBuyers / customerSegmentation.totalCustomers) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
                <div className="w-full bg-orange-100 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-green-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: customerSegmentation.totalCustomers > 0 ? `${(customerSegmentation.repeatBuyers / customerSegmentation.totalCustomers) * 100}%` : '0%' }}
                  ></div>
                </div>
                <div className="mt-2 text-center text-xs text-gray-500">
                  ลูกค้าทั้งหมด {customerSegmentation.totalCustomers.toLocaleString('th-TH')} ราย
                </div>
              </div>

            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">ไม่สามารถโหลดข้อมูลได้</div>
          )}
        </div>

        {/* Top Products Card - ย้ายมาไว้ล่างสุด */}
        <div 
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-6 cursor-pointer hover:shadow-xl transition-shadow duration-300"
          onClick={onNavigateToTopProducts}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center">
                <img src="/order.png" alt="Order" className="w-10 h-10" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">ข้อมูลสินค้าขายดี (แบ่งตามประเภท)</h3>
                <p className="text-xs text-gray-600">
                  {formatDateForDisplay(dateRange.startDate)} ถึง {formatDateForDisplay(dateRange.endDate)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.keys(categorizedProducts).length > 0 ? (
              Object.entries(categorizedProducts).map(([factoryType, products]) => (
                <div key={factoryType} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Category Header */}
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <h4 className="font-semibold text-blue-800 text-sm">
                          {factoryType}
                        </h4>
                        <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                          {products.length} รายการ
                        </span>
                      </div>
                    </div>
                    {/* Total Summary */}
                    <div className="text-left text-xs mt-2">
                      <div className="mb-1">
                        <span className="text-gray-600">รวมจำนวน:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                        <span className="font-semibold text-blue-800">
                          {products.reduce((sum, product) => sum + product.total_quantity, 0).toLocaleString()} รายการ
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">รวมยอดขาย:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                        <span className="font-semibold text-green-600">
                          {products.reduce((sum, product) => sum + product.net_amount, 0).toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} บาท
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Products Table */}
                  <div className="overflow-x-auto h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">
                            สินค้า
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">
                            <div className="text-center">
                              <div>จำนวน</div>
                              <div>ยอดขาย</div>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {products.map((product, index) => (
                          <tr key={`${factoryType}-${index}`} className="hover:bg-gray-50">
                            <td className="px-3 py-2 w-1/2">
                              <div className="flex items-start gap-2 max-w-full">
                                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-blue-500 rounded-full flex-shrink-0">
                                  {index + 1}
                                </span>
                                <div className="min-w-0 flex-1 overflow-hidden w-32">
                                  <p className="text-xs font-medium text-gray-900 truncate w-full" title={product.product_code}>
                                    {product.product_code}
                                  </p>
                                  <p className="text-xs text-gray-600 truncate w-full" title={product.product_description}>
                                    {product.product_description}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 w-1/2 text-right">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-green-600 truncate">
                                  {product.net_amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  จำนวน: {product.total_quantity.toLocaleString()}
                                </p>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 col-span-full">
                <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-500">ไม่มีข้อมูลสินค้า</p>
              </div>
            )}
          </div>
        </div>


      </div>
    </div>
  );
};

export default DashboardComponent;